/**
 * A multi-week unit plan in the three stages of Understanding by Design
 * (Wiggins and McTighe): desired results, assessment evidence, then the learning plan.
 *
 * The teacher supplies the content. This module arranges it: it counts the lessons, places
 * the lesson titles in order across the weeks, gives each lesson a place in the arc of the
 * unit (launch, build, apply, check, revise, assess) with the WHERETO elements that stage of
 * the unit usually serves, and reports which parts of the plan are still empty. Nothing is
 * written for the teacher.
 */

export const MIN_WEEKS = 1;
export const MAX_WEEKS = 12;
export const MIN_PER_WEEK = 1;
export const MAX_PER_WEEK = 7;
export const MIN_MINUTES = 20;
export const MAX_MINUTES = 120;
export const MINUTES_STEP = 5;
export const DEFAULT_WEEKS = 4;
export const DEFAULT_PER_WEEK = 4;
export const DEFAULT_MINUTES = 45;
export const MAX_ITEMS = 12;

const LINE_BREAK = /\r?\n/;

export interface UnitInputs {
  title: string;
  subject: string;
  grade: string;
  weeks: number;
  perWeek: number;
  minutes: number;
  goals: string;
  understandings: string;
  questions: string;
  knowledge: string;
  skills: string;
  taskTitle: string;
  taskDescription: string;
  evidence: string;
  lessons: string;
}

export const EMPTY_INPUTS: UnitInputs = {
  title: "",
  subject: "",
  grade: "",
  weeks: DEFAULT_WEEKS,
  perWeek: DEFAULT_PER_WEEK,
  minutes: DEFAULT_MINUTES,
  goals: "",
  understandings: "",
  questions: "",
  knowledge: "",
  skills: "",
  taskTitle: "",
  taskDescription: "",
  evidence: "",
  lessons: "",
};

function clamp(
  value: number,
  low: number,
  high: number,
  fallback: number
): number {
  if (!Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(high, Math.max(low, Math.round(value)));
}

export const clampWeeks = (v: number) =>
  clamp(v, MIN_WEEKS, MAX_WEEKS, DEFAULT_WEEKS);
export const clampPerWeek = (v: number) =>
  clamp(v, MIN_PER_WEEK, MAX_PER_WEEK, DEFAULT_PER_WEEK);
export const clampMinutes = (v: number) =>
  clamp(v, MIN_MINUTES, MAX_MINUTES, DEFAULT_MINUTES);

/** The non-blank lines of a field, tidied, at most `MAX_ITEMS` (lesson titles are not capped here). */
export function itemsOf(text: string, limit: number = MAX_ITEMS): string[] {
  return text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .slice(0, limit);
}

/* ------------------------------------------------------------------ the arc */

export type Phase =
  | "launch"
  | "build"
  | "apply"
  | "checkin"
  | "revise"
  | "assess";

/** The WHERETO elements each stage of the unit usually serves. */
export type Element =
  | "where"
  | "hook"
  | "equip"
  | "rethink"
  | "evaluate"
  | "tailor"
  | "organize";

export const WHERETO_ORDER: readonly Element[] = [
  "where",
  "hook",
  "equip",
  "rethink",
  "evaluate",
  "tailor",
  "organize",
];

export const PHASE_ELEMENTS: Record<Phase, readonly Element[]> = {
  launch: ["where", "hook"],
  build: ["equip"],
  apply: ["equip", "tailor"],
  checkin: ["evaluate", "rethink"],
  revise: ["rethink", "tailor"],
  assess: ["evaluate"],
};

/**
 * Where lesson `index` (0-based) of `total` sits in the unit. The first lesson launches it
 * and the last assesses it. With five or more lessons the one before the last revises; with
 * six or more, the middle lesson is a check-in. Everything else builds in the first 40 per
 * cent of the unit and applies after that.
 */
export function phaseFor(index: number, total: number): Phase {
  if (total <= 1 || index <= 0) {
    return "launch";
  }
  if (index >= total - 1) {
    return "assess";
  }
  if (total >= 5 && index === total - 2) {
    return "revise";
  }
  if (total >= 6 && index === Math.floor(total / 2)) {
    return "checkin";
  }
  return index / (total - 1) < 0.4 ? "build" : "apply";
}

export interface Lesson {
  number: number;
  week: number;
  /** Lesson within its week, from 1. */
  day: number;
  title: string;
  phase: Phase;
}

export type CheckKey =
  | "goals"
  | "understandings"
  | "questions"
  | "knowledge"
  | "skills"
  | "task"
  | "evidence"
  | "lessons";

export interface Check {
  key: CheckKey;
  done: boolean;
}

export interface UnitPlan {
  weeks: number;
  perWeek: number;
  total: number;
  minutes: number;
  totalMinutes: number;
  lessons: Lesson[];
  /** Lesson titles typed beyond the number of lessons in the unit. */
  extraTitles: number;
  checks: Check[];
}

export function buildPlan(inputs: UnitInputs): UnitPlan {
  const weeks = clampWeeks(inputs.weeks);
  const perWeek = clampPerWeek(inputs.perWeek);
  const minutes = clampMinutes(inputs.minutes);
  const total = weeks * perWeek;
  const titles = itemsOf(inputs.lessons, Number.MAX_SAFE_INTEGER);
  const lessons = Array.from({ length: total }, (_, i) => ({
    number: i + 1,
    week: Math.floor(i / perWeek) + 1,
    day: (i % perWeek) + 1,
    title: titles[i] ?? "",
    phase: phaseFor(i, total),
  }));
  const has = (text: string) => itemsOf(text).length > 0;
  const checks: Check[] = [
    { key: "goals", done: has(inputs.goals) },
    { key: "understandings", done: has(inputs.understandings) },
    { key: "questions", done: has(inputs.questions) },
    { key: "knowledge", done: has(inputs.knowledge) },
    { key: "skills", done: has(inputs.skills) },
    {
      key: "task",
      done:
        inputs.taskTitle.trim() !== "" || inputs.taskDescription.trim() !== "",
    },
    { key: "evidence", done: has(inputs.evidence) },
    { key: "lessons", done: titles.length > 0 },
  ];
  return {
    weeks,
    perWeek,
    total,
    minutes,
    totalMinutes: total * minutes,
    lessons,
    extraTitles: Math.max(0, titles.length - total),
    checks,
  };
}

/** The lessons of one week, in order. */
export function weekLessons(plan: UnitPlan, week: number): Lesson[] {
  return plan.lessons.filter((lesson) => lesson.week === week);
}

/** Whole hours and leftover minutes in a number of minutes. */
export function hoursAndMinutes(totalMinutes: number): {
  hours: number;
  minutes: number;
} {
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

/* ----------------------------------------------------------------- documents */

export interface PlanTable {
  head: string[];
  rows: string[][];
}

export interface PlanSection {
  heading: string;
  level: 2 | 3;
  paragraphs: string[];
  items: string[];
  table?: PlanTable;
}

/** Labels the document needs; the widget supplies them from its strings. */
export interface PlanLabels {
  summary: string;
  stage1: string;
  stage2: string;
  stage3: string;
  goals: string;
  understandings: string;
  questions: string;
  knowledge: string;
  skills: string;
  task: string;
  evidence: string;
  toComplete: string;
  weekHeading: string;
  tableHead: [string, string, string, string];
  untitled: string;
  phases: Record<Phase, string>;
  elements: Record<Element, string>;
}

function listSection(
  heading: string,
  text: string,
  empty: string,
  level: 2 | 3 = 3
): PlanSection {
  const items = itemsOf(text);
  return {
    heading,
    level,
    paragraphs: [],
    items: items.length > 0 ? items : [empty],
  };
}

function weekSection(
  plan: UnitPlan,
  week: number,
  labels: PlanLabels,
  numberText: (n: number) => string
): PlanSection {
  return {
    heading: labels.weekHeading.replace("{week}", numberText(week)),
    level: 3,
    paragraphs: [],
    items: [],
    table: {
      head: [...labels.tableHead],
      rows: weekLessons(plan, week).map((lesson) => [
        numberText(lesson.number),
        lesson.title === "" ? labels.untitled : lesson.title,
        labels.phases[lesson.phase],
        PHASE_ELEMENTS[lesson.phase]
          .map((el) => labels.elements[el])
          .join(", "),
      ]),
    },
  };
}

/** The whole document as sections, ready to render, copy or download. */
export function planSections(
  inputs: UnitInputs,
  plan: UnitPlan,
  labels: PlanLabels,
  numberText: (n: number) => string = String
): PlanSection[] {
  const task: string[] = [];
  if (inputs.taskTitle.trim() !== "") {
    task.push(inputs.taskTitle.trim());
  }
  if (inputs.taskDescription.trim() !== "") {
    task.push(inputs.taskDescription.trim());
  }
  return [
    {
      heading:
        inputs.title.trim() === "" ? labels.untitled : inputs.title.trim(),
      level: 2,
      paragraphs: [labels.summary],
      items: [],
    },
    { heading: labels.stage1, level: 2, paragraphs: [], items: [] },
    listSection(labels.goals, inputs.goals, labels.toComplete),
    listSection(
      labels.understandings,
      inputs.understandings,
      labels.toComplete
    ),
    listSection(labels.questions, inputs.questions, labels.toComplete),
    listSection(labels.knowledge, inputs.knowledge, labels.toComplete),
    listSection(labels.skills, inputs.skills, labels.toComplete),
    { heading: labels.stage2, level: 2, paragraphs: [], items: [] },
    {
      heading: labels.task,
      level: 3,
      paragraphs: task.length > 0 ? task : [labels.toComplete],
      items: [],
    },
    listSection(labels.evidence, inputs.evidence, labels.toComplete),
    { heading: labels.stage3, level: 2, paragraphs: [], items: [] },
    ...Array.from({ length: plan.weeks }, (_, i) =>
      weekSection(plan, i + 1, labels, numberText)
    ),
  ];
}

/** Plain text (Markdown-style headings and tables) for copying or saving. */
export function planToText(sections: readonly PlanSection[]): string {
  const blocks: string[] = [];
  for (const section of sections) {
    blocks.push(`${"#".repeat(section.level)} ${section.heading}`);
    blocks.push(...section.paragraphs);
    if (section.items.length > 0) {
      blocks.push(section.items.map((item) => `- ${item}`).join("\n"));
    }
    if (section.table) {
      const line = (cells: readonly string[]) => `| ${cells.join(" | ")} |`;
      blocks.push(
        [
          line(section.table.head),
          line(section.table.head.map(() => "---")),
          ...section.table.rows.map(line),
        ].join("\n")
      );
    }
  }
  return `${blocks.join("\n\n")}\n`;
}

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};
const ESCAPABLE = /[&<>"]/g;

function escapeHtml(text: string): string {
  return text.replace(ESCAPABLE, (char) => ESCAPES[char] ?? char);
}

/** The same document as HTML, for pasting into a word processor or learning platform. */
export function planToHtml(sections: readonly PlanSection[]): string {
  const parts: string[] = [];
  for (const section of sections) {
    const tag = `h${section.level}`;
    parts.push(`<${tag}>${escapeHtml(section.heading)}</${tag}>`);
    for (const paragraph of section.paragraphs) {
      parts.push(`<p>${escapeHtml(paragraph)}</p>`);
    }
    if (section.items.length > 0) {
      const list = section.items.map((item) => `<li>${escapeHtml(item)}</li>`);
      parts.push(`<ul>${list.join("")}</ul>`);
    }
    if (section.table) {
      const head = section.table.head.map(
        (cell) => `<th>${escapeHtml(cell)}</th>`
      );
      const rows = section.table.rows.map(
        (row) =>
          `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`
      );
      parts.push(
        `<table border="1" cellpadding="4" cellspacing="0"><thead><tr>${head.join("")}</tr></thead><tbody>${rows.join("")}</tbody></table>`
      );
    }
  }
  return parts.join("\n");
}
